(() => {
  const IMAGE_SELECTOR = [
    '#MainContent product-card .product-media-container img',
    '#MainContent .crux-series-showcase__media img',
    '#MainContent img[loading="eager"]',
    '#MainContent img[fetchpriority="high"]'
  ].join(',');
  const GROUP_SELECTOR = '#MainContent .product-grid, #MainContent .resource-list';
  const READY_CLASS = 'crux-image-ready';
  const BOUND_ATTRIBUTE = 'data-crux-image-bound';
  const GROUP_BOUND_ATTRIBUTE = 'data-crux-priority-bound';
  const CARD_BOUND_ATTRIBUTE = 'data-crux-card-priority-bound';
  const preloads = new WeakMap();
  let scheduled = false;
  const groupObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      promoteVisibleCards(entry.target);
      groupObserver.unobserve(entry.target);
    });
  }, { rootMargin: '100% 0px' });
  const cardObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      if (window.matchMedia('(max-width: 749px)').matches) promoteCard(entry.target, true);
      cardObserver.unobserve(entry.target);
    });
  }, { rootMargin: '40% 25%' });

  function reveal(image) {
    if (!(image instanceof HTMLImageElement)) return;
    const finish = () => image.classList.add(READY_CLASS);
    if (image.complete && image.naturalWidth > 0) {
      if (typeof image.decode === 'function') image.decode().catch(() => {}).finally(finish);
      else finish();
      return;
    }
    image.addEventListener('load', () => {
      if (typeof image.decode === 'function') image.decode().catch(() => {}).finally(finish);
      else finish();
    }, { once: true });
    image.addEventListener('error', finish, { once: true });
  }

  function bindImage(image) {
    if (!(image instanceof HTMLImageElement) || image.hasAttribute(BOUND_ATTRIBUTE)) return;
    image.setAttribute(BOUND_ATTRIBUTE, '');
    reveal(image);
  }

  function firstCardImage(card) {
    return card.querySelector('.product-media-container img');
  }

  function preloadImage(image) {
    if (image.complete || preloads.has(image)) return;
    const preload = new Image();
    preload.fetchPriority = 'high';
    preload.decoding = 'async';
    if (image.sizes) preload.sizes = image.sizes;
    if (image.srcset) preload.srcset = image.srcset;
    preload.src = image.currentSrc || image.src;
    preloads.set(image, preload);
  }

  function promoteCard(card, highPriority) {
    const image = firstCardImage(card);
    if (!image) return;
    image.loading = 'eager';
    image.fetchPriority = highPriority ? 'high' : 'auto';
    bindImage(image);
    if (highPriority && window.matchMedia('(max-width: 749px)').matches) preloadImage(image);
  }

  function promoteVisibleCards(group) {
    if (!(group instanceof Element) || group.getClientRects().length === 0) return;
    const limit = window.matchMedia('(max-width: 749px)').matches ? 2 : 4;
    const highPriorityLimit = window.matchMedia('(max-width: 749px)').matches ? 2 : 1;
    const cards = Array.from(group.querySelectorAll('product-card')).filter(
      (card) => card.getClientRects().length > 0
    );
    cards.slice(0, limit).forEach((card, index) => {
      promoteCard(card, index < highPriorityLimit);
    });
  }

  function bindGroup(group) {
    if (!(group instanceof Element) || group.hasAttribute(GROUP_BOUND_ATTRIBUTE)) return;
    group.setAttribute(GROUP_BOUND_ATTRIBUTE, '');
    groupObserver.observe(group);
  }

  function bindCard(card) {
    if (!(card instanceof Element) || card.hasAttribute(CARD_BOUND_ATTRIBUTE)) return;
    card.setAttribute(CARD_BOUND_ATTRIBUTE, '');
    cardObserver.observe(card);
  }

  function scan(root = document) {
    if (root instanceof HTMLImageElement && root.matches(IMAGE_SELECTOR)) bindImage(root);
    if (root instanceof Element || root instanceof Document) {
      root.querySelectorAll(IMAGE_SELECTOR).forEach(bindImage);
      if (root instanceof Element && root.matches(GROUP_SELECTOR)) bindGroup(root);
      root.querySelectorAll(GROUP_SELECTOR).forEach(bindGroup);
      if (root instanceof Element && root.matches('product-card')) bindCard(root);
      root.querySelectorAll('product-card').forEach(bindCard);
    }
  }

  function scheduleScan() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      scan(document);
    });
  }

  function start() {
    window.clearTimeout(window.__cruxImageLoadingFailsafe);
    scan(document);
    const main = document.getElementById('MainContent');
    if (main) {
      new MutationObserver(scheduleScan).observe(main, { childList: true, subtree: true });
    }
    window.addEventListener('pageshow', scheduleScan);
    window.matchMedia('(max-width: 749px)').addEventListener?.('change', scheduleScan);
    document.addEventListener('shopify:section:load', scheduleScan);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
